// Renders referee match-report forms into self-contained, print-optimized HTML
// documents: one A4 page per football match (see
// `features/football-tournaments.md`). Each page carries the match header, the
// referee line, both team rosters (padded with blank rows for late squad
// additions), half-time/full-time score boxes, blank goals and cards grids, a
// penalty shoot-out grid on knock-out forms, and the referee/captain signature
// lines. The output is a plain white page the referee fills in by hand —
// deliberately independent of the app's dark MUI theme so it prints cleanly
// with black ink on white paper.
//
// This is a pure string builder (no DOM, no React) so it can be unit-tested.
// The thin window/print wiring lives in `printScoreSheet.ts`.
//
// Forms are localised through the shared i18n core: callers pass the active
// `Locale` (defaulting to English) and every fixed label resolves through the
// `admin.refereeForm.*` catalogue keys.

import type { RefereeFormTeam, RefereeMatchForm } from '../domain';
import {
  translate,
  DEFAULT_LOCALE,
  LOCALE_META,
  type Locale,
  type TranslateParams,
} from '../i18n/messages';

/** A `translate` bound to one locale, rooted at the referee-form namespace. */
type FormTranslator = (key: string, params?: TranslateParams) => string;

function formTranslator(locale: Locale): FormTranslator {
  return (key, params) => translate(locale, `admin.refereeForm.${key}`, params);
}

/** Escapes text for safe interpolation into HTML. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Minimum roster rows printed per team. Squads bigger than this get one row
 * per member; smaller squads are padded with blank numbered rows so names
 * added after printing can still be written in by hand.
 */
const ROSTER_MIN_ROWS = 12;

/** Blank rows of the goals grid — enough for a very one-sided match. */
const GOAL_ROWS = 8;

/** Blank rows of the cards grid. */
const CARD_ROWS = 6;

/** Regular kicks of a penalty shoot-out; one extra cell covers sudden death. */
const SHOOTOUT_KICKS = 5;

/** The match headline: "Group A — Match 3" or the bracket node's own label. */
function matchTitle(form: RefereeMatchForm, t: FormTranslator): string {
  if (form.kind === 'group') {
    return t('matchTitleGroup', {
      label: form.poolLabel ?? '',
      order: form.matchOrder ?? 0,
    });
  }
  return form.nodeLabel ?? form.matchId;
}

/** Group matches carry a slot/pitch meta line; bracket matches have none. */
function metaLine(form: RefereeMatchForm, t: FormTranslator): string {
  if (form.kind !== 'group' || form.timeSlot === null || form.tableIndex === null) {
    return '';
  }
  return `
        <div class="meta">${escapeHtml(
          t('slotPitch', { timeSlot: form.timeSlot, pitch: form.tableIndex }),
        )}</div>`;
}

/** The referee line: the assigned name pre-filled, or a blank underline. */
function refereeLine(form: RefereeMatchForm, t: FormTranslator): string {
  const value =
    form.refereeName !== null
      ? `<span class="referee-name">${escapeHtml(form.refereeName)}</span>`
      : '<span class="blank referee-blank"></span>';
  return `
      <div class="referee-line">
        <span class="referee-label">${escapeHtml(t('refereeLabel'))}</span>
        ${value}
      </div>`;
}

/**
 * One team's numbered roster table: a member per row, padded with blank rows
 * up to `ROSTER_MIN_ROWS` so late squad additions can be written in.
 */
function rosterTable(team: RefereeFormTeam, t: FormTranslator): string {
  const rowCount = Math.max(ROSTER_MIN_ROWS, team.members.length);
  const rows = Array.from({ length: rowCount }, (_, i) => {
    const member = team.members[i];
    return `
            <tr>
              <td class="num">${i + 1}</td>
              <td class="member">${member === undefined ? '' : escapeHtml(member)}</td>
            </tr>`;
  }).join('');
  return `<table class="roster">
          <thead>
            <tr>
              <th class="num">${escapeHtml(t('numberHeader'))}</th>
              <th class="member">${escapeHtml(team.name)}</th>
            </tr>
          </thead>
          <tbody>${rows}
          </tbody>
        </table>`;
}

/** One labelled score line of the result section: two blank boxes. */
function scoreLine(labelKey: string, t: FormTranslator): string {
  return `
        <div class="score-line">
          <span class="score-label">${escapeHtml(t(labelKey))}</span>
          <span class="score-box"></span>
          <span class="score-sep">:</span>
          <span class="score-box"></span>
        </div>`;
}

/** A squad member as printed on an event row: "3. Name" ('' for a blank row). */
function eventMember(number: number, name: string): string {
  return name === '' ? `${number}.` : `${number}. ${name}`;
}

/**
 * The goals grid: minute / team / scorer. Recorded goals pre-fill the top rows
 * (own goals marked), the rest stay blank for handwriting — so an unrecorded
 * match prints the familiar empty grid and a recorded one a report of record.
 */
function goalsGrid(form: RefereeMatchForm, t: FormTranslator): string {
  const rowCount = Math.max(GOAL_ROWS, form.goals.length);
  const rows = Array.from({ length: rowCount }, (_, i) => {
    const goal = form.goals[i];
    if (goal === undefined) {
      return `
            <tr>
              <td class="minute"></td>
              <td class="team"></td>
              <td class="scorer"></td>
            </tr>`;
    }
    const scorer = eventMember(goal.scorerNumber, goal.scorerName);
    const ownGoal = goal.ownGoal ? ` ${t('ownGoalMark')}` : '';
    return `
            <tr>
              <td class="minute">${goal.minute ?? ''}</td>
              <td class="team">${escapeHtml(goal.teamName)}</td>
              <td class="scorer">${escapeHtml(`${scorer}${ownGoal}`)}</td>
            </tr>`;
  }).join('');
  return `<table class="goals">
          <thead>
            <tr>
              <th class="minute">${escapeHtml(t('minuteHeader'))}</th>
              <th class="team">${escapeHtml(t('teamHeader'))}</th>
              <th class="scorer">${escapeHtml(t('scorerHeader'))}</th>
            </tr>
          </thead>
          <tbody>${rows}
          </tbody>
        </table>`;
}

/**
 * The cards grid: minute / team / player / card. Recorded bookings pre-fill
 * the top rows (Y/R marks localised), the rest stay blank for handwriting.
 */
function cardsGrid(form: RefereeMatchForm, t: FormTranslator): string {
  const rowCount = Math.max(CARD_ROWS, form.cards.length);
  const rows = Array.from({ length: rowCount }, (_, i) => {
    const card = form.cards[i];
    if (card === undefined) {
      return `
            <tr>
              <td class="minute"></td>
              <td class="team"></td>
              <td class="card-player"></td>
              <td class="card"></td>
            </tr>`;
    }
    const mark = card.card === 'YELLOW' ? t('yellowMark') : t('redMark');
    return `
            <tr>
              <td class="minute">${card.minute ?? ''}</td>
              <td class="team">${escapeHtml(card.teamName)}</td>
              <td class="card-player">${escapeHtml(
                eventMember(card.playerNumber, card.playerName),
              )}</td>
              <td class="card">${escapeHtml(mark)}</td>
            </tr>`;
  }).join('');
  return `<table class="cards">
          <thead>
            <tr>
              <th class="minute">${escapeHtml(t('minuteHeader'))}</th>
              <th class="team">${escapeHtml(t('teamHeader'))}</th>
              <th class="card-player">${escapeHtml(t('playerHeader'))}</th>
              <th class="card">${escapeHtml(t('cardHeader'))}</th>
            </tr>
          </thead>
          <tbody>${rows}
          </tbody>
        </table>`;
}

/**
 * The penalty shoot-out grid (knock-out forms only): a row per team with five
 * regular kick cells plus one extra cell for sudden death.
 */
function shootoutGrid(form: RefereeMatchForm, t: FormTranslator): string {
  const kickHeaders = Array.from(
    { length: SHOOTOUT_KICKS },
    (_, i) => `<th class="kick">${i + 1}</th>`,
  ).join('');
  const teamRow = (team: RefereeFormTeam): string => `
            <tr>
              <th class="team">${escapeHtml(team.name)}</th>
              ${Array.from(
                { length: SHOOTOUT_KICKS + 1 },
                () => '<td class="kick"></td>',
              ).join('')}
            </tr>`;
  return `
      <h2 class="section-title">${escapeHtml(t('shootoutTitle'))}</h2>
      <table class="shootout">
        <thead>
          <tr>
            <th class="team">${escapeHtml(t('teamHeader'))}</th>
            ${kickHeaders}
            <th class="kick kick--extra">${escapeHtml(t('shootoutExtraHeader'))}</th>
          </tr>
        </thead>
        <tbody>${teamRow(form.teamA)}${teamRow(form.teamB)}
        </tbody>
      </table>`;
}

/** One blank signature line; captains carry their team name next to the label. */
function signatureLine(label: string, teamName: string | null): string {
  const team =
    teamName === null
      ? ''
      : `<span class="signature-team">${escapeHtml(teamName)}</span>`;
  return `
        <div class="signature">
          <span class="signature-label">${escapeHtml(label)}</span>
          ${team}
          <span class="blank signature-blank"></span>
        </div>`;
}

/** The full single-match referee form (one printed A4 page). */
function formSheet(form: RefereeMatchForm, t: FormTranslator): string {
  return `
    <section class="sheet">
      <header class="sheet-head">
        <div class="tournament">${escapeHtml(form.tournamentName)}</div>
        <h1 class="match-title">${escapeHtml(matchTitle(form, t))}</h1>${metaLine(form, t)}
      </header>
${refereeLine(form, t)}

      <h2 class="section-title">${escapeHtml(t('rostersTitle'))}</h2>
      <div class="rosters">
        ${rosterTable(form.teamA, t)}
        ${rosterTable(form.teamB, t)}
      </div>

      <h2 class="section-title">${escapeHtml(t('resultTitle'))}</h2>
      <div class="result">${scoreLine('halfTimeLabel', t)}${scoreLine('fullTimeLabel', t)}
      </div>

      <h2 class="section-title">${escapeHtml(t('goalsTitle'))}</h2>
      ${goalsGrid(form, t)}

      <h2 class="section-title">${escapeHtml(t('cardsTitle'))}</h2>
      ${cardsGrid(form, t)}
${form.hasShootoutSection ? shootoutGrid(form, t) : ''}

      <h2 class="section-title">${escapeHtml(t('signaturesTitle'))}</h2>
      <div class="signatures">${signatureLine(t('refereeSignature'), null)}${signatureLine(t('captainSignature'), form.teamA.name)}${signatureLine(t('captainSignature'), form.teamB.name)}
      </div>
    </section>`;
}

const STYLES = `
    :root { color-scheme: light; }
    * { box-sizing: border-box; }
    html, body { background: #fff; color: #000; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      font-size: 11px;
      line-height: 1.3;
      padding: 16px;
      /* Keep header / column shading when printed (off by default in browsers). */
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .sheet { max-width: 760px; margin: 0 auto 24px; }
    .sheet-head { border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 10px; }
    .tournament { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #444; }
    .match-title { font-size: 22px; margin: 2px 0 0; }
    .meta { font-size: 11px; color: #444; margin-top: 2px; }
    .section-title { font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; margin: 12px 0 4px; }
    .blank { display: inline-block; min-width: 180px; border-bottom: 1px solid #000; height: 14px; vertical-align: bottom; }
    .referee-line { font-size: 12px; margin: 8px 0; }
    .referee-label { font-weight: 700; margin-right: 6px; }
    .referee-name { border-bottom: 1px solid #000; padding: 0 24px 1px 2px; }
    table { border-collapse: collapse; width: 100%; }
    .rosters { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; align-items: start; }
    .roster th, .roster td { border: 1px solid #999; padding: 2px 5px; text-align: left; }
    .roster thead th { background: #eee; font-size: 10px; }
    .roster .num { width: 26px; text-align: center; color: #444; }
    .roster .member { height: 17px; overflow-wrap: anywhere; }
    .result { display: flex; gap: 32px; margin: 4px 0; }
    .score-line { display: flex; align-items: center; gap: 6px; font-size: 11px; }
    .score-label { font-weight: 600; }
    .score-box { display: inline-block; width: 34px; height: 24px; border: 1px solid #000; }
    .score-sep { font-weight: 700; }
    .goals th, .goals td, .cards th, .cards td { border: 1px solid #999; padding: 2px 5px; text-align: left; }
    .goals thead th, .cards thead th { background: #eee; font-size: 10px; }
    .goals .minute, .cards .minute { width: 46px; }
    .goals .team, .cards .team { width: 150px; }
    .goals td, .cards td { height: 17px; }
    .cards .card { width: 60px; }
    .shootout th, .shootout td { border: 1px solid #999; padding: 2px 5px; text-align: center; }
    .shootout thead th { background: #eee; font-size: 10px; }
    .shootout .team { text-align: left; overflow-wrap: anywhere; }
    .shootout .kick { width: 40px; height: 20px; }
    .signatures { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 14px; }
    .signature { font-size: 10px; }
    .signature-label { display: block; font-weight: 600; }
    .signature-team { display: block; color: #444; overflow-wrap: anywhere; }
    .signature .blank { min-width: 0; width: 100%; margin-top: 16px; }
    @media print {
      body { padding: 0; }
      .sheet { max-width: none; margin: 0; }
      .sheet:not(:last-child) { page-break-after: always; }
    }
    @page { size: A4; margin: 12mm; }
`;

/** Wraps rendered form sections into a complete standalone HTML document. */
function formDocument(title: string, body: string, locale: Locale): string {
  return `<!DOCTYPE html>
<html lang="${LOCALE_META[locale].code}">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(title)}</title>
    <style>${STYLES}</style>
  </head>
  <body>
${body}
  </body>
</html>`;
}

/**
 * Builds a complete printable HTML document for the given referee match-report
 * forms, one A4 page per match, in `locale` (English when omitted). Safe to
 * write into a new window or iframe and print.
 */
export function renderRefereeFormHtml(
  forms: readonly RefereeMatchForm[],
  locale: Locale = DEFAULT_LOCALE,
): string {
  const t = formTranslator(locale);
  const title = forms[0]
    ? t('docTitle', { name: forms[0].tournamentName })
    : t('docTitleEmpty');
  const body = forms.length
    ? forms.map((form) => formSheet(form, t)).join('\n')
    : `<p class="empty">${escapeHtml(t('noForms'))}</p>`;
  return formDocument(title, body, locale);
}
